import React, { useState, useCallback } from 'react';
import {
  Download,
  FileSpreadsheet,
  Loader2,
  Users,
  UserCheck,
  Briefcase,
  Globe,
  Calendar,
  CalendarDays,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import PillSelect from '../../../components/UI/PillSelect';
import toast from '../../../utils/toast';
import api from '../../../services/api';

const FISCAL_YEARS = (() => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const years = [];
  // Most recent completed FY: if we're in Apr+ of 2026, FY2025 (Apr 2025–Mar 2026) just ended.
  // If we're in Jan–Mar 2026, FY2024 (Apr 2024–Mar 2025) is the most recent completed.
  const latestCompletedFY = currentMonth >= 3 ? currentYear - 1 : currentYear - 2;
  for (let fy = latestCompletedFY; fy >= latestCompletedFY - 4; fy--) {
    years.push({
      value: String(fy),
      label: `FY${fy} (Apr ${fy} – Mar ${fy + 1})`,
      period_start: `${fy}-04-01`,
      period_end: `${fy + 1}-03-31`,
      snapshot_date: `${fy + 1}-03-31`,
    });
  }
  return years;
})();

const FY_OPTIONS = FISCAL_YEARS.map(fy => ({ value: fy.value, label: fy.label }));

const StatCard = ({ label, value, sub, icon: Icon, color = 'red' }) => {
  const colorMap = {
    red: { bg: 'bg-red-100', text: 'text-red-600' },
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
  };
  const c = colorMap[color] || colorMap.red;
  return (
    <div className="bg-white rounded-2xl shadow-sm border p-5">
      <div className="flex items-center">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${c.bg}`}>
          <Icon className={`w-6 h-6 ${c.text}`} />
        </div>
        <div className="ml-4">
          <p className="text-2xl font-bold text-gray-900">{value ?? '-'}</p>
          <p className="text-sm text-gray-600">{label}</p>
          {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
        </div>
      </div>
    </div>
  );
};

const SectionHeader = ({ title, icon: Icon, expanded, onToggle, badge }) => (
  <button
    onClick={onToggle}
    className="w-full flex items-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-left font-semibold text-gray-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
  >
    {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
    <Icon className="w-4 h-4 text-gray-500" />
    <span>{title}</span>
    {badge && <span className="ml-auto text-sm font-normal text-gray-500">{badge}</span>}
  </button>
);

const AGE_BRACKET_LABELS = {
  under_20: '~19',
  '20_29': '20~29',
  '30_39': '30~39',
  '40_49': '40~49',
  '50_59': '50~59',
  '60_69': '60~69',
  '70_plus': '70~',
};

const HQManpowerReport = () => {
  const [selectedFY, setSelectedFY] = useState(FISCAL_YEARS[0]?.value || '');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [sections, setSections] = useState({
    headcount: true,
    age: true,
    movement: true,
    nationality: true,
  });

  const fyConfig = FISCAL_YEARS.find(fy => fy.value === String(selectedFY));

  const handleGenerate = useCallback(async () => {
    if (!fyConfig) return;
    setLoading(true);
    try {
      const res = await api.get('/reporting/hq-manpower-report/', {
        params: {
          snapshot_date: fyConfig.snapshot_date,
          period_start: fyConfig.period_start,
          period_end: fyConfig.period_end,
        },
      });
      setReportData(res.data);
      const total = res.data.headcount?.total || 0;
      toast.success(`Report generated — ${total} employees`);
      if (res.data.warnings?.length > 0) {
        toast.warning(`${res.data.warnings.length} warning(s) — check details`, { icon: '⚠️' });
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to generate report';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [fyConfig]);

  const handleExport = useCallback(async () => {
    if (!fyConfig) return;
    setExporting(true);
    try {
      const res = await api.get('/reporting/hq-manpower-report/export/', {
        params: {
          snapshot_date: fyConfig.snapshot_date,
          period_start: fyConfig.period_start,
          period_end: fyConfig.period_end,
        },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Exeo Group - Yearly Manpower Reporting ${fyConfig.snapshot_date.split('-').reverse().join('.')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel file downloaded');
    } catch (err) {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [fyConfig]);

  const toggleSection = (key) => {
    setSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const hc = reportData?.headcount;
  const gb = reportData?.gender_breakdown;
  const age = reportData?.age_structure;
  const mv = reportData?.movement;
  const nat = reportData?.nationality_breakdown;

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="flex items-center gap-3">
        <PillSelect
          options={FY_OPTIONS}
          value={selectedFY}
          onChange={(val) => {
            setSelectedFY(val);
            setReportData(null);
          }}
          placeholder="Select Fiscal Year"
          icon={Calendar}
          iconColor="text-red-600"
          theme="admin"
        />
        <button
          onClick={handleGenerate}
          disabled={loading}
          className="btn btn-admin-primary"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
          {loading ? 'Generating...' : 'Generate Report'}
        </button>
        {reportData && (
          <button
            onClick={handleExport}
            disabled={exporting}
            className="btn btn-admin-secondary"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {exporting ? 'Exporting...' : 'Download Excel'}
          </button>
        )}
      </div>

      {/* Empty state */}
      {!reportData && !loading && (
        <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Globe className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">HQ Manpower Report</h3>
          <p className="text-gray-600 mb-1">Exeo Group Yearly Manpower Reporting</p>
          <p className="text-sm text-gray-500">Select a fiscal year and click "Generate Report" to preview the data.</p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
          <Loader2 className="w-8 h-8 text-red-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Generating report data...</p>
        </div>
      )}

      {/* Report Data */}
      {reportData && !loading && (
        <div className="space-y-5">
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total Headcount" value={hc?.total} icon={Users} color="red" />
            <StatCard label="Managers" value={hc?.managers} sub={`${gb?.manager?.women || 0} women`} icon={UserCheck} color="purple" />
            <StatCard label="Employees" value={hc?.employees} sub={`${gb?.employee?.women || 0} women`} icon={Briefcase} color="blue" />
            <StatCard
              label="Avg. Service"
              value={reportData.average_service_years || '-'}
              sub={reportData.average_age ? `Avg. age: ${reportData.average_age}` : undefined}
              icon={CalendarDays}
              color="emerald"
            />
          </div>

          {/* Headcount & Gender */}
          <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
            <SectionHeader
              title="Headcount & Gender Breakdown"
              icon={Users}
              expanded={sections.headcount}
              onToggle={() => toggleSection('headcount')}
              badge={`${hc?.total} total`}
            />
            {sections.headcount && (
              <div className="p-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-red-600 text-white text-xs">
                        <th className="px-4 py-2 text-left font-medium">Category</th>
                        <th className="px-4 py-2 text-right font-medium">Total</th>
                        <th className="px-4 py-2 text-right font-medium">Women</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      <tr className="table-row-dense">
                        <td className="px-4 py-2 font-medium text-gray-700">Board of Directors (役員)</td>
                        <td className="px-4 py-2 text-right">{hc?.directors}</td>
                        <td className="px-4 py-2 text-right text-gray-400">—</td>
                      </tr>
                      <tr className="table-row-dense">
                        <td className="px-4 py-2 font-medium text-gray-700">Manager (管理職)</td>
                        <td className="px-4 py-2 text-right">{hc?.managers}</td>
                        <td className="px-4 py-2 text-right">{gb?.manager?.women}</td>
                      </tr>
                      <tr className="table-row-dense">
                        <td className="px-4 py-2 font-medium text-gray-700">Employee (一般社員)</td>
                        <td className="px-4 py-2 text-right">{hc?.employees}</td>
                        <td className="px-4 py-2 text-right">{gb?.employee?.women}</td>
                      </tr>
                      <tr className="table-row-dense">
                        <td className="px-4 py-2 font-medium text-gray-700">Temporary Worker (契約社員)</td>
                        <td className="px-4 py-2 text-right">{hc?.temporary_workers}</td>
                        <td className="px-4 py-2 text-right">{gb?.temporary?.women}</td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-100 font-semibold text-sm">
                        <td className="px-4 py-2">Total</td>
                        <td className="px-4 py-2 text-right">{hc?.total}</td>
                        <td className="px-4 py-2 text-right">{(gb?.manager?.women || 0) + (gb?.employee?.women || 0) + (gb?.temporary?.women || 0)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Age Structure */}
          <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
            <SectionHeader
              title="Age Structure & Averages"
              icon={CalendarDays}
              expanded={sections.age}
              onToggle={() => toggleSection('age')}
              badge={reportData.average_age ? `Avg: ${reportData.average_age} years` : null}
            />
            {sections.age && (
              <div className="p-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-gray-900">{reportData.average_age ?? '-'}</p>
                    <p className="text-xs text-gray-500">Average Age</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-gray-900">{reportData.average_service_years || '-'}</p>
                    <p className="text-xs text-gray-500">Avg. Service Length</p>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-red-600 text-white text-xs">
                        {Object.values(AGE_BRACKET_LABELS).map(label => (
                          <th key={label} className="px-3 py-2 text-center font-medium">{label}</th>
                        ))}
                        <th className="px-3 py-2 text-center font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="table-row-dense">
                        {Object.keys(AGE_BRACKET_LABELS).map(key => (
                          <td key={key} className="px-3 py-2 text-center">{age?.[key] ?? 0}</td>
                        ))}
                        <td className="px-3 py-2 text-center font-semibold">
                          {Object.keys(AGE_BRACKET_LABELS).reduce((sum, key) => sum + (age?.[key] ?? 0), 0)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Movement */}
          <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
            <SectionHeader
              title="Workforce Movement"
              icon={TrendingUp}
              expanded={sections.movement}
              onToggle={() => toggleSection('movement')}
              badge={`+${mv?.increases?.hiring?.total || 0} / -${mv?.decreases?.resignation_retirement?.total || 0}`}
            />
            {sections.movement && (
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Increases */}
                  <div>
                    <h4 className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700 mb-2">
                      <TrendingUp className="w-4 h-4" /> Increases
                    </h4>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-emerald-600 text-white text-xs">
                          <th className="px-3 py-2 text-left font-medium">Type</th>
                          <th className="px-3 py-2 text-right font-medium">Total</th>
                          <th className="px-3 py-2 text-right font-medium">Women</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        <tr className="table-row-dense">
                          <td className="px-3 py-2">Hiring</td>
                          <td className="px-3 py-2 text-right">{mv?.increases?.hiring?.total}</td>
                          <td className="px-3 py-2 text-right">{mv?.increases?.hiring?.women}</td>
                        </tr>
                        <tr className="table-row-dense text-gray-400">
                          <td className="px-3 py-2">Seconded workers accepted</td>
                          <td className="px-3 py-2 text-right">{mv?.increases?.secondment_in?.total}</td>
                          <td className="px-3 py-2 text-right">{mv?.increases?.secondment_in?.women}</td>
                        </tr>
                        <tr className="table-row-dense text-gray-400">
                          <td className="px-3 py-2">Returning from secondment</td>
                          <td className="px-3 py-2 text-right">{mv?.increases?.returning_from_secondment?.total}</td>
                          <td className="px-3 py-2 text-right">{mv?.increases?.returning_from_secondment?.women}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Decreases */}
                  <div>
                    <h4 className="flex items-center gap-1.5 text-sm font-semibold text-red-700 mb-2">
                      <TrendingDown className="w-4 h-4" /> Decreases
                    </h4>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-red-600 text-white text-xs">
                          <th className="px-3 py-2 text-left font-medium">Type</th>
                          <th className="px-3 py-2 text-right font-medium">Total</th>
                          <th className="px-3 py-2 text-right font-medium">Women</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        <tr className="table-row-dense">
                          <td className="px-3 py-2">Resignation / Retirement</td>
                          <td className="px-3 py-2 text-right">{mv?.decreases?.resignation_retirement?.total}</td>
                          <td className="px-3 py-2 text-right">{mv?.decreases?.resignation_retirement?.women}</td>
                        </tr>
                        <tr className="table-row-dense text-gray-400">
                          <td className="px-3 py-2">Returning to parent co.</td>
                          <td className="px-3 py-2 text-right">{mv?.decreases?.returning_to_parent?.total}</td>
                          <td className="px-3 py-2 text-right">{mv?.decreases?.returning_to_parent?.women}</td>
                        </tr>
                        <tr className="table-row-dense text-gray-400">
                          <td className="px-3 py-2">Seconded out</td>
                          <td className="px-3 py-2 text-right">{mv?.decreases?.seconded_out?.total}</td>
                          <td className="px-3 py-2 text-right">{mv?.decreases?.seconded_out?.women}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Nationality */}
          <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
            <SectionHeader
              title="Nationality Breakdown"
              icon={Globe}
              expanded={sections.nationality}
              onToggle={() => toggleSection('nationality')}
              badge={nat ? `${Object.keys(nat).filter(k => nat[k] > 0).length} nationalities` : null}
            />
            {sections.nationality && nat && (
              <div className="p-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-red-600 text-white text-xs">
                        <th className="px-3 py-2 text-left font-medium">Nationality</th>
                        <th className="px-3 py-2 text-right font-medium">Count</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {Object.entries(nat)
                        .filter(([, count]) => count > 0)
                        .sort(([, a], [, b]) => b - a)
                        .map(([nationality, count]) => (
                          <tr key={nationality} className="table-row-dense">
                            <td className="px-3 py-2">{nationality}</td>
                            <td className="px-3 py-2 text-right font-medium">{count}</td>
                          </tr>
                        ))
                      }
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-100 font-semibold text-sm">
                        <td className="px-3 py-2">Total</td>
                        <td className="px-3 py-2 text-right">
                          {Object.values(nat).reduce((sum, c) => sum + c, 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Warnings */}
          {reportData.warnings?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <h4 className="flex items-center gap-1.5 text-sm font-semibold text-amber-800 mb-2">
                <AlertTriangle className="w-4 h-4" /> Data Warnings ({reportData.warnings.length})
              </h4>
              <ul className="text-xs text-amber-700 space-y-1 max-h-40 overflow-y-auto">
                {reportData.warnings.map((w, i) => (
                  <li key={i}>• {w}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HQManpowerReport;
